import { useCallback, useRef } from 'react';

import { SuggestionOption } from '../types';

interface UseFocusManagementProps {
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  filteredSuggestions: SuggestionOption[];
  setIsOpen: (isOpen: boolean) => void;
}

export const useFocusManagement = ({
  onFocus,
  onBlur,
  filteredSuggestions,
  setIsOpen,
}: UseFocusManagementProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      onFocus?.(e);

      // Show suggestions if we have any filtered suggestions available
      if (filteredSuggestions.length > 0) {
        setIsOpen(true);
      }
    },
    [onFocus, filteredSuggestions.length, setIsOpen]
  );

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlur?.(e);

      // Use relatedTarget to check if focus is moving within our component
      const relatedTarget = e.relatedTarget as Element | null;
      const container = containerRef.current;

      // If focus is moving to an element within our container, don't close
      if (container && relatedTarget && container.contains(relatedTarget)) {
        return;
      }

      // Use a minimal timeout only as a fallback for browsers that don't support relatedTarget properly
      blurTimeoutRef.current = setTimeout(() => {
        const activeElement = document.activeElement;

        // Final check: if focus is still within our container, don't close
        if (container && activeElement && container.contains(activeElement)) {
          return;
        }

        setIsOpen(false);
        blurTimeoutRef.current = null;
      }, 0);
    },
    [onBlur, setIsOpen]
  );

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  return {
    containerRef,
    handleInputFocus,
    handleInputBlur,
    cleanup,
  };
};
